import React,{useState,useEffect,useCallback} from 'react';
import {dashboardAPI,branchAPI} from '../utils/api';
import {PageHeader,Badge,DataTable,fmt,fmtDate} from '../components/common';
import {useLang} from '../i18n';
import {useAuth} from '../context/AuthContext';
import {DollarSign,AlertTriangle,Users,Package,ShoppingCart,Calendar,
  RefreshCw,Building2,Filter,X,TrendingUp,Truck,ArrowLeftRight,
  Award,Calculator,ChevronRight} from 'lucide-react';
import toast from 'react-hot-toast';
import {useNavigate} from 'react-router-dom';

const today=()=>new Date().toISOString().split('T')[0];
const monthStart=()=>{const d=new Date();d.setDate(1);return d.toISOString().split('T')[0];};
const fmtLabel=(df,dt)=>{
  if(!df&&!dt)return null;
  const f=d=>new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'});
  if(df&&dt)return`${f(df)} — ${f(dt)}`;
  if(df)return`From ${f(df)}`;
  return`Until ${f(dt)}`;
};
const PRESETS=[
  {label:'Today',df:today(),dt:today()},
  {label:'This Month',df:monthStart(),dt:today()},
  {label:'Last 30d',df:(()=>{const d=new Date();d.setDate(d.getDate()-30);return d.toISOString().split('T')[0]})(),dt:today()},
  {label:'All Time',df:'',dt:''},
];

// ── KPI Card ──────────────────────────────────────────────────────
function KpiCard({label,value,sub,icon:Icon,color,badge,onClick,warn}){
  return(
    <div onClick={onClick} style={{
      background:'var(--surface)',border:`1px solid ${warn?'rgba(239,68,68,.3)':'var(--border)'}`,
      borderLeft:`3px solid ${color}`,borderRadius:12,padding:'14px 16px',
      cursor:onClick?'pointer':'default',transition:'transform .1s',
    }}
    onMouseEnter={e=>{if(onClick)e.currentTarget.style.transform='translateY(-2px)'}}
    onMouseLeave={e=>{if(onClick)e.currentTarget.style.transform='none'}}>
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:8}}>
        <div style={{width:32,height:32,borderRadius:8,background:color+'20',display:'flex',alignItems:'center',justifyContent:'center'}}>
          <Icon size={16} color={color}/>
        </div>
        {badge&&<span style={{fontSize:9,padding:'2px 6px',borderRadius:10,background:warn?'var(--danger)':'var(--warning)',color:'#fff',fontWeight:700}}>{badge}</span>}
        {onClick&&<ChevronRight size={12} color="var(--td)"/>}
      </div>
      <div style={{fontSize:10,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.5,marginBottom:4}}>{label}</div>
      <div style={{fontSize:20,fontWeight:900,color:warn?'var(--danger)':color}}>{value}</div>
      {sub&&<div style={{fontSize:11,color:'var(--td)',marginTop:3}}>{sub}</div>}
    </div>
  );
}

// ── Section Header ────────────────────────────────────────────────
function SectionTitle({children,icon:Icon,color='var(--primary)'}){
  return(
    <div style={{display:'flex',alignItems:'center',gap:7,marginBottom:12}}>
      <Icon size={15} color={color}/>
      <span style={{fontSize:14,fontWeight:700}}>{children}</span>
    </div>
  );
}

export default function DashboardPage(){
  const{t}=useLang();
  const{isOwner,myBranchId,hasPermission}=useAuth();
  const navigate=useNavigate();
  const[stats,setStats]=useState(null);
  const[ld,setLd]=useState(true);
  const[branches,setBranches]=useState([]);
  const[selectedBranch,setSelectedBranch]=useState('');
  const[df,setDf]=useState(monthStart());
  const[dt,setDt]=useState(today());
  const[showFilters,setShowFilters]=useState(false);

  useEffect(()=>{
    if(isOwner) branchAPI.getAll({limit:100}).then(r=>setBranches(r.data.data||[])).catch(()=>{});
  },[isOwner]);

  const load=useCallback(async()=>{
    setLd(true);
    try{
      const p={};
      if(df) p.date_from=df;
      if(dt) p.date_to=dt;
      if(isOwner&&selectedBranch) p.branch_id=selectedBranch;
      const r=await dashboardAPI.getStats(p);
      setStats(r.data);
    }catch{toast.error(t('common.failedToLoad'));}
    finally{setLd(false);}
  },[df,dt,selectedBranch,isOwner]);

  useEffect(()=>{load();},[load]);

  const clearFilters=()=>{setDf(monthStart());setDt(today());setSelectedBranch('');};
  const hasCustomFilter=df!==monthStart()||dt!==today()||selectedBranch;

  if(ld&&!stats)return<div className="text-muted" style={{padding:60,textAlign:'center'}}><RefreshCw size={20} style={{animation:'spin 1s linear infinite',margin:'0 auto 8px',display:'block'}}/>{t('common.loading')}</div>;
  if(!stats)return null;

  const{summary:s,invoices_by_status=[],recent_orders=[]}=stats;
  const STATUS_COLORS={paid:'var(--success)',sent:'var(--info)',partial:'var(--warning)',overdue:'var(--danger)',draft:'var(--td)'};
  const STATUS_BADGE={completed:'success',processing:'info',pending:'warning',draft:'primary',cancelled:'danger'};
  const dateLabel=fmtLabel(df,dt);
  const activeBranchName=isOwner&&selectedBranch?branches.find(b=>String(b.id)===String(selectedBranch))?.name:null;

  return(
    <div>
      <PageHeader
        title={t('dashboard.title')}
        subtitle={t('dashboard.subtitle')}
        action={
          <div style={{display:'flex',gap:8}}>
            <button className={`btn ${showFilters?'btn-primary':'btn-ghost'}`} onClick={()=>setShowFilters(v=>!v)} style={{display:'flex',alignItems:'center',gap:6,fontSize:12}}>
              <Filter size={13}/> {t('common.filter')}
              {hasCustomFilter&&<span style={{width:7,height:7,borderRadius:'50%',background:'var(--warning)',display:'inline-block'}}/>}
            </button>
          </div>
        }
      />

      {/* Filter panel */}
      {showFilters&&(
        <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,padding:'14px 16px',marginBottom:16,display:'flex',flexWrap:'wrap',gap:12,alignItems:'flex-end'}}>
          <div>
            <div style={{fontSize:10,fontWeight:700,color:'var(--td)',marginBottom:6,letterSpacing:.5}}>QUICK RANGE</div>
            <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
              {PRESETS.map(p=>(
                <button key={p.label} className="btn btn-sm"
                  style={{fontSize:11,padding:'4px 10px',background:df===p.df&&dt===p.dt?'var(--primary)':'var(--s2)',color:df===p.df&&dt===p.dt?'#fff':'var(--td)',border:`1px solid ${df===p.df&&dt===p.dt?'var(--primary)':'var(--border)'}`}}
                  onClick={()=>{setDf(p.df);setDt(p.dt);}}>{p.label}</button>
              ))}
            </div>
          </div>
          <div>
            <div style={{fontSize:10,fontWeight:700,color:'var(--td)',marginBottom:6,letterSpacing:.5}}>{t('common.fromDate')}</div>
            <input type="date" value={df} onChange={e=>setDf(e.target.value)} style={{padding:'7px 10px',borderRadius:8,border:'1px solid var(--border)',background:'var(--s2)',color:'var(--text)',fontSize:12}}/>
          </div>
          <div>
            <div style={{fontSize:10,fontWeight:700,color:'var(--td)',marginBottom:6,letterSpacing:.5}}>{t('common.toDate')}</div>
            <input type="date" value={dt} onChange={e=>setDt(e.target.value)} style={{padding:'7px 10px',borderRadius:8,border:'1px solid var(--border)',background:'var(--s2)',color:'var(--text)',fontSize:12}}/>
          </div>
          {isOwner&&branches.length>0&&(
            <div>
              <div style={{fontSize:10,fontWeight:700,color:'var(--td)',marginBottom:6,letterSpacing:.5}}>{t('common.branch').toUpperCase()}</div>
              <select value={selectedBranch} onChange={e=>setSelectedBranch(e.target.value)} style={{padding:'7px 10px',borderRadius:8,border:'1px solid var(--border)',background:'var(--s2)',color:'var(--text)',fontSize:12,minWidth:160}}>
                <option value="">{t('reports.allBranches')}</option>
                {branches.map(b=><option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          )}
          <div style={{display:'flex',gap:8,alignItems:'flex-end',paddingBottom:1}}>
            <button className="btn btn-primary btn-sm" onClick={load} disabled={ld} style={{height:34,fontSize:12}}>
              {ld?<RefreshCw size={12} style={{animation:'spin 1s linear infinite'}}/>:<Filter size={12}/>} {t('reports.runReport')}
            </button>
            {hasCustomFilter&&<button className="btn btn-ghost btn-sm" onClick={clearFilters} style={{height:34,fontSize:12}}><X size={12}/> {t('common.clear')}</button>}
          </div>
        </div>
      )}

      {/* Active filter chips */}
      {(dateLabel||activeBranchName)&&(
        <div style={{display:'flex',gap:8,marginBottom:12,flexWrap:'wrap',alignItems:'center'}}>
          <span style={{fontSize:11,color:'var(--td)'}}>Showing:</span>
          {dateLabel&&<span style={{display:'inline-flex',alignItems:'center',gap:5,fontSize:11,fontWeight:600,padding:'3px 10px',borderRadius:20,background:'rgba(99,102,241,.1)',color:'var(--primary)',border:'1px solid rgba(99,102,241,.2)'}}><Calendar size={10}/>{dateLabel}</span>}
          {activeBranchName&&<span style={{display:'inline-flex',alignItems:'center',gap:5,fontSize:11,fontWeight:600,padding:'3px 10px',borderRadius:20,background:'rgba(0,212,170,.1)',color:'var(--success)',border:'1px solid rgba(0,212,170,.2)'}}><Building2 size={10}/>{activeBranchName}</span>}
        </div>
      )}

      {/* ── ROW 1: Core KPIs ── */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:12,marginBottom:16}}>
        <KpiCard label={t('dashboard.revenue')} value={fmt(s.total_revenue)} sub={t('dashboard.paidInvoices')} icon={DollarSign} color="var(--success)"/>
        <KpiCard label={t('dashboard.outstanding')} value={fmt(s.total_outstanding)} sub={t('dashboard.unpaid')} icon={AlertTriangle} color="var(--warning)" warn={s.total_outstanding>0}/>
        <KpiCard label={t('dashboard.customers')} value={s.total_customers} sub={`${s.branch_count} ${t('common.branch').toLowerCase()}`} icon={Users} color="var(--primary)" onClick={()=>navigate('/customers')}/>
        <KpiCard label={t('dashboard.products')} value={s.total_products} sub={`${s.low_stock_count} low stock`} icon={Package} color="var(--accent)" badge={s.low_stock_count>0?`${s.low_stock_count} low`:null} onClick={()=>navigate('/inventory')}/>
        <KpiCard label={t('dashboard.posToday')} value={fmt(s.pos_today_total)} sub={`${s.pos_today_count} txns today`} icon={ShoppingCart} color="var(--info)" onClick={()=>navigate('/pos')}/>
      </div>

      {/* ── ROW 2: New Modules ── */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:12,marginBottom:16}}>
        {/* Purchase Orders */}
        {hasPermission('purchase_orders.view')&&(
          <KpiCard
            label="Purchase Orders"
            value={s.po_count||0}
            sub={s.po_unpaid_amount>0?`${fmt(s.po_unpaid_amount)} unpaid`:'All paid'}
            icon={Truck}
            color="var(--primary)"
            badge={s.po_unpaid_count>0?`${s.po_unpaid_count} unpaid`:null}
            warn={s.po_unpaid_count>0}
            onClick={()=>navigate('/purchase-orders')}
          />
        )}
        {/* Stock Transfers */}
        {hasPermission('stock_transfers.view')&&(
          <KpiCard
            label="Stock Transfers"
            value={s.st_count||0}
            sub={s.st_in_transit>0?`${s.st_in_transit} in transit`:s.st_pending>0?`${s.st_pending} pending`:'All complete'}
            icon={ArrowLeftRight}
            color="var(--accent)"
            badge={(s.st_pending||0)+(s.st_in_transit||0)>0?`${(s.st_pending||0)+(s.st_in_transit||0)} active`:null}
            onClick={()=>navigate('/stock-transfers')}
          />
        )}
        {/* Item Commissions */}
        {(hasPermission('item_commissions.view')||hasPermission('item_commissions.view_own'))&&(
          <KpiCard
            label="Rep Commissions"
            value={fmt(s.comm_rep_amount||0)}
            sub={`${s.comm_count||0} sales · Biz ${fmt(s.comm_business_amount||0)}`}
            icon={Award}
            color="var(--warning)"
            badge={s.comm_unassigned_reps>0?`${s.comm_unassigned_reps} unassigned`:null}
            warn={s.comm_unassigned_reps>0}
            onClick={()=>navigate('/item-commissions')}
          />
        )}
        {/* Net Profit */}
        {(hasPermission('cost_price.view'))&&s.comm_net_profit>0&&(
          <KpiCard
            label="Net Profit"
            value={fmt(s.comm_net_profit||0)}
            sub={`Branch ${fmt(s.comm_branch_amount||0)}`}
            icon={TrendingUp}
            color="var(--success)"
            onClick={()=>navigate('/reports')}
          />
        )}
      </div>

      {/* ── ROW 3: Orders + Invoice Status ── */}
      <div className="grid-2" style={{marginBottom:16}}>
        {/* Recent Orders */}
        <div className="card">
          <SectionTitle icon={ShoppingCart} color="var(--primary)">{t('dashboard.recentOrders')}</SectionTitle>
          <DataTable
            columns={[
              {key:'order_number',label:'#',render:r=><span className="text-primary font-bold" style={{cursor:'pointer'}} onClick={()=>navigate('/orders')}>{r.order_number}</span>},
              {key:'customer',label:t('common.customer'),render:r=>r.customer?.name||'—'},
              {key:'order_date',label:t('common.date'),render:r=>fmtDate(r.order_date)},
              {key:'total_amount',label:t('common.total'),render:r=><span className="font-bold">{fmt(r.total_amount)}</span>},
              {key:'status',label:t('common.status'),render:r=><Badge variant={STATUS_BADGE[r.status]||'primary'}>{r.status}</Badge>},
            ]}
            data={recent_orders}/>
        </div>

        {/* Invoice Status */}
        <div className="card">
          <SectionTitle icon={DollarSign} color="var(--success)">{t('dashboard.invoiceStatus')}</SectionTitle>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(100px,1fr))',gap:10}}>
            {['paid','sent','partial','overdue','draft'].map(st=>{
              const item=invoices_by_status.find(i=>i.status===st);
              const cnt=parseInt(item?.count||0);
              const tot=parseFloat(item?.total||0);
              const color=STATUS_COLORS[st];
              return(
                <div key={st} style={{padding:12,borderRadius:10,background:color+'10',border:`1px solid ${color}25`,cursor:'pointer'}} onClick={()=>navigate('/invoices')}>
                  <div style={{fontSize:9,color,fontWeight:700,letterSpacing:.5,textTransform:'uppercase'}}>{st}</div>
                  <div style={{fontSize:22,fontWeight:900,margin:'3px 0 1px',color}}>{cnt}</div>
                  <div style={{fontSize:11,color:'var(--td)'}}>{fmt(tot)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── ROW 4: Commission breakdown (if data) ── */}
      {s.comm_count>0&&(hasPermission('item_commissions.view')||isOwner)&&(
        <div className="card" style={{marginBottom:16}}>
          <SectionTitle icon={Calculator} color="var(--warning)">Item Commission Breakdown</SectionTitle>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))',gap:10}}>
            {[
              ['Net Profit','comm_net_profit','var(--success)'],
              ['Business $','comm_business_amount','var(--accent)'],
              ['Branch $','comm_branch_amount','var(--primary)'],
              ['Rep Commissions','comm_rep_amount','var(--warning)'],
            ].map(([label,key,color])=>(
              <div key={key} style={{padding:14,borderRadius:10,background:'var(--s2)',border:'1px solid var(--border)'}}>
                <div style={{fontSize:10,color:'var(--td)',fontWeight:700,textTransform:'uppercase',letterSpacing:.4,marginBottom:4}}>{label}</div>
                <div style={{fontSize:18,fontWeight:800,color}}>{fmt(s[key]||0)}</div>
              </div>
            ))}
            {s.comm_unassigned_reps>0&&(
              <div style={{padding:14,borderRadius:10,background:'rgba(239,68,68,0.08)',border:'1px solid rgba(239,68,68,.25)',cursor:'pointer'}} onClick={()=>navigate('/item-commissions')}>
                <div style={{fontSize:10,color:'var(--danger)',fontWeight:700,textTransform:'uppercase',letterSpacing:.4,marginBottom:4}}>⚠ Unassigned Reps</div>
                <div style={{fontSize:18,fontWeight:800,color:'var(--danger)'}}>{s.comm_unassigned_reps}</div>
                <div style={{fontSize:10,color:'var(--danger)',marginTop:2}}>Click to assign →</div>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
